import { APPEAL_STATUS_LABEL } from '@repo/types';

export const APPEAL_STATUS_TEXT_COLOR = {
  PRE_APPEAL: { light: '#374151', dark: '#D3D3D3' },
  TO_BE_APPEALED: { light: '#3b82f5', dark: '#3b82f5' },
  APPEAL_SUBMITTED: { light: '#1E40AF', dark: '#DBEAFE' },
  NO_APPEAL: { light: '#5B21B6', dark: '#E9D5FF' },
  APPEAL_APPROVED: { light: '#065F46', dark: '#D1FAE5' },
  APPEAL_DENIED: { light: '#991B1B', dark: '#FEE2E2' },
  DEADLINE_EXPIRED: { light: '#991B1B', dark: '#FECACA' },
  CLOSED: { light: '#4B5563', dark: '#D1D5DB' },
  AWAITING_APPEAL_SUBMISSION: { light: '#804F00', dark: '#FBF3C8' },
  PARTIAL_WRITE_OFF: { light: '#804F00', dark: '#FBF3C8' },
} as const;

export const EXCLUDED_STATUS_FILTER_KEYS = ['APPEAL_SUBMITTED_BACKLOG'];

export const APPEAL_STATUS_OPTIONS = {
  light: {
    PRE_APPEAL: {
      value: 'PRE_APPEAL',
      label: APPEAL_STATUS_LABEL.PRE_APPEAL,
      color: APPEAL_STATUS_TEXT_COLOR.PRE_APPEAL.light,
    },
    TO_BE_APPEALED: {
      value: 'TO_BE_APPEALED',
      label: APPEAL_STATUS_LABEL.TO_BE_APPEALED,
      color: APPEAL_STATUS_TEXT_COLOR.TO_BE_APPEALED.light,
    },
    AWAITING_APPEAL_SUBMISSION: {
      value: 'AWAITING_APPEAL_SUBMISSION',
      label: APPEAL_STATUS_LABEL.AWAITING_APPEAL_SUBMISSION,
      color: APPEAL_STATUS_TEXT_COLOR.AWAITING_APPEAL_SUBMISSION.light,
    },
    APPEAL_SUBMITTED: {
      value: 'APPEAL_SUBMITTED',
      label: APPEAL_STATUS_LABEL.APPEAL_SUBMITTED,
      color: APPEAL_STATUS_TEXT_COLOR.APPEAL_SUBMITTED.light,
    },
    APPEAL_SUBMITTED_BACKLOG: {
      value: 'APPEAL_SUBMITTED',
      label: 'Appeal Submitted (Backlog)',
      color: APPEAL_STATUS_TEXT_COLOR.APPEAL_SUBMITTED.light,
    },
    NO_APPEAL: {
      value: 'NO_APPEAL',
      label: APPEAL_STATUS_LABEL.NO_APPEAL,
      color: APPEAL_STATUS_TEXT_COLOR.NO_APPEAL.light,
    },
    APPEAL_APPROVED: {
      value: 'APPEAL_APPROVED',
      label: APPEAL_STATUS_LABEL.APPEAL_APPROVED,
      color: APPEAL_STATUS_TEXT_COLOR.APPEAL_APPROVED.light,
    },
    APPEAL_DENIED: {
      value: 'APPEAL_DENIED',
      label: APPEAL_STATUS_LABEL.APPEAL_DENIED,
      color: APPEAL_STATUS_TEXT_COLOR.APPEAL_DENIED.light,
    },
    DEADLINE_EXPIRED: {
      value: 'DEADLINE_EXPIRED',
      label: APPEAL_STATUS_LABEL.DEADLINE_EXPIRED,
      color: APPEAL_STATUS_TEXT_COLOR.DEADLINE_EXPIRED.light,
    },
    CLOSED: {
      value: 'CLOSED',
      label: APPEAL_STATUS_LABEL.CLOSED,
      color: APPEAL_STATUS_TEXT_COLOR.CLOSED.light,
    },
  },
  dark: {
    PRE_APPEAL: {
      value: 'PRE_APPEAL',
      label: APPEAL_STATUS_LABEL.PRE_APPEAL,
      color: APPEAL_STATUS_TEXT_COLOR.PRE_APPEAL.dark,
      bgColor: APPEAL_STATUS_TEXT_COLOR.PRE_APPEAL.light,
    },
    TO_BE_APPEALED: {
      value: 'TO_BE_APPEALED',
      label: APPEAL_STATUS_LABEL.TO_BE_APPEALED,
      color: APPEAL_STATUS_TEXT_COLOR.TO_BE_APPEALED.dark,
      bgColor: APPEAL_STATUS_TEXT_COLOR.TO_BE_APPEALED.light,
    },
    AWAITING_APPEAL_SUBMISSION: {
      value: 'AWAITING_APPEAL_SUBMISSION',
      label: APPEAL_STATUS_LABEL.AWAITING_APPEAL_SUBMISSION,
      color: APPEAL_STATUS_TEXT_COLOR.AWAITING_APPEAL_SUBMISSION.dark,
      bgColor: APPEAL_STATUS_TEXT_COLOR.AWAITING_APPEAL_SUBMISSION.light,
    },
    APPEAL_SUBMITTED: {
      value: 'APPEAL_SUBMITTED',
      label: APPEAL_STATUS_LABEL.APPEAL_SUBMITTED,
      color: APPEAL_STATUS_TEXT_COLOR.APPEAL_SUBMITTED.dark,
      bgColor: APPEAL_STATUS_TEXT_COLOR.APPEAL_SUBMITTED.light,
    },
    APPEAL_SUBMITTED_BACKLOG: {
      value: 'APPEAL_SUBMITTED',
      label: 'Appeal Submitted (Backlog)',
      color: APPEAL_STATUS_TEXT_COLOR.APPEAL_SUBMITTED.dark,
      bgColor: APPEAL_STATUS_TEXT_COLOR.APPEAL_SUBMITTED.light,
    },
    NO_APPEAL: {
      value: 'NO_APPEAL',
      label: APPEAL_STATUS_LABEL.NO_APPEAL,
      color: APPEAL_STATUS_TEXT_COLOR.NO_APPEAL.dark,
      bgColor: APPEAL_STATUS_TEXT_COLOR.NO_APPEAL.light,
    },
    APPEAL_APPROVED: {
      value: 'APPEAL_APPROVED',
      label: APPEAL_STATUS_LABEL.APPEAL_APPROVED,
      color: APPEAL_STATUS_TEXT_COLOR.APPEAL_APPROVED.dark,
      bgColor: APPEAL_STATUS_TEXT_COLOR.APPEAL_APPROVED.light,
    },
    APPEAL_DENIED: {
      value: 'APPEAL_DENIED',
      label: APPEAL_STATUS_LABEL.APPEAL_DENIED,
      color: APPEAL_STATUS_TEXT_COLOR.APPEAL_DENIED.dark,
      bgColor: APPEAL_STATUS_TEXT_COLOR.APPEAL_DENIED.light,
    },
    DEADLINE_EXPIRED: {
      value: 'DEADLINE_EXPIRED',
      label: APPEAL_STATUS_LABEL.DEADLINE_EXPIRED,
      color: APPEAL_STATUS_TEXT_COLOR.DEADLINE_EXPIRED.dark,
      bgColor: APPEAL_STATUS_TEXT_COLOR.DEADLINE_EXPIRED.light,
    },
    CLOSED: {
      value: 'CLOSED',
      label: APPEAL_STATUS_LABEL.CLOSED,
      color: APPEAL_STATUS_TEXT_COLOR.CLOSED.dark,
      bgColor: APPEAL_STATUS_TEXT_COLOR.CLOSED.light,
    },
  },
};

/**
 * Color palette for clerk assignment badges.
 * Each color has a light and dark variant for theme support.
 * Colors are chosen to be visually distinct from appeal status colors.
 */
export const CLERK_COLOR_PALETTE = [
  { light: '#0891B2', dark: '#22D3EE' }, // Cyan
  { light: '#7C3AED', dark: '#A78BFA' }, // Violet
  { light: '#059669', dark: '#34D399' }, // Emerald
  { light: '#DC2626', dark: '#F87171' }, // Red
  { light: '#D97706', dark: '#FBBF24' }, // Amber
  { light: '#4F46E5', dark: '#818CF8' }, // Indigo
  { light: '#DB2777', dark: '#F472B6' }, // Pink
  { light: '#0D9488', dark: '#2DD4BF' }, // Teal
  { light: '#9333EA', dark: '#C084FC' }, // Purple
  { light: '#EA580C', dark: '#FB923C' }, // Orange
] as const;

/**
 * Default color for clerks not found in the color map (unmatched cases).
 * Uses a neutral gray to indicate an unknown/unassigned state.
 */
export const CLERK_DEFAULT_COLOR = { light: '#6B7280', dark: '#9CA3AF' } as const; // Gray
